import { motion } from "framer-motion";
import { MapPin, User, Dumbbell, Users } from "lucide-react";

const ABOUT_IMG =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_coaching-book/artifacts/ouu8lq85_3559B3B1-6180-4C67-B3F9-5AAFAFBB1F13.webp";

const FACTS = [
  { icon: MapPin, label: "Based in", value: "Belfast / Newtownabbey" },
  { icon: User, label: "Format", value: "1-to-1 sessions" },
  { icon: Dumbbell, label: "Focus", value: "Football & Fitness" },
  { icon: Users, label: "For", value: "All ages & levels" },
];

const rise = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function About() {
  return (
    <section id="about-me" data-testid="about" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
      <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
        {/* Image */}
        <motion.div
          variants={rise}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="md:col-span-5"
        >
          <div className="relative overflow-hidden aspect-[4/5] border border-white/10">
            <motion.img
              initial={{ scale: 1.15 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
              src={ABOUT_IMG}
              alt="Joel Green — JG Coaching"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-[#2E6BE6] mix-blend-color opacity-30 pointer-events-none" />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
          </div>
        </motion.div>

        {/* Text */}
        <motion.div
          variants={rise}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="md:col-span-7"
        >
          <span className="text-[#2E6BE6] text-xs uppercase tracking-[0.3em]">About</span>
          <h2 className="font-display font-bold uppercase tracking-tighter text-4xl md:text-6xl mt-4 leading-[0.95]">
            Meet your coach
          </h2>
          <div className="mt-6 space-y-4 text-white/70 text-base md:text-lg font-body max-w-2xl">
            <p>
              I'm <span className="text-white font-semibold">Joel Green</span>, the coach behind
              <span className="text-white font-semibold"> JG Coaching</span> — a Belfast-based football and fitness
              coach helping players and athletes develop and get sharper, fitter and more confident along the way.
            </p>
            <p>
              Every session is one-to-one and built around <span className="text-white font-semibold">you</span>:
              your goals, your level and your body. Whether you want a better first touch, more pace and power, or a
              structured plan to stay consistent — we'll get there together, one focused 60-minute session at a time.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10" data-testid="about-facts">
            {FACTS.map((f) => (
              <div key={f.label} className="border border-white/10 p-4 bg-black/30 backdrop-blur-sm">
                <f.icon className="w-5 h-5 text-[#D4AF37]" />
                <p className="text-xs uppercase tracking-[0.15em] text-white/40 mt-3">{f.label}</p>
                <p className="text-white font-semibold text-sm mt-0.5">{f.value}</p>
              </div>
            ))}
          </div>

          <button
            data-testid="about-cta"
            onClick={() => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" })}
            className="group inline-flex items-center gap-3 mt-10 bg-[#D4AF37] text-black px-7 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:-translate-y-0.5 transition-transform"
          >
            Book your session
            <span className="transition-transform group-hover:translate-x-1">→</span>
          </button>
        </motion.div>
      </div>
    </section>
  );
}
